import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Loader2,
  FileCode2,
  Monitor,
  CheckCircle2,
  Clock,
  AlertTriangle,
  BarChart3,
  Shield,
  Award,
  RefreshCw,
  ArrowUpRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusDot = ({ color }) => (
  <span
    className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
    style={{ backgroundColor: color }}
  />
);

const StatCard = ({ icon: Icon, label, value, sub, color, testId }) => (
  <Card
    className="bg-white border border-[#e5e7eb] hover:border-[#00AEEF]/40 transition-colors"
    data-testid={testId}
  >
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-[#6b7280] uppercase tracking-wider mb-1">
            {label}
          </p>
          <p className="text-2xl font-bold text-[#1f2937]">{value}</p>
          {sub && <p className="text-xs text-[#9ca3af] mt-1">{sub}</p>}
        </div>
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const CompletionBar = ({ label, completed, total, color }) => {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="text-[#4b5563] font-medium">{label}</span>
        <span className="text-[#6b7280]">
          {completed}/{total} ({pct}%)
        </span>
      </div>
      <div className="h-2 bg-[#f3f4f6] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

export default function TechnicalAssessmentTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getAuthHeaders = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem("executiveToken")}`,
    },
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(
        `${API}/executive/ta-hos-stats`,
        getAuthHeaders()
      );
      setData(res.data);
    } catch (err) {
      console.error("Error fetching TA/HOS stats:", err);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div
        className="flex items-center justify-center py-24"
        data-testid="ta-tab-loading"
      >
        <Loader2 className="w-6 h-6 animate-spin text-[#00AEEF]" />
        <span className="ml-2 text-sm text-[#6b7280]">
          Loading technical assessment data...
        </span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className="flex flex-col items-center justify-center py-24 gap-3"
        data-testid="ta-tab-error"
      >
        <AlertTriangle className="w-8 h-8 text-amber-500" />
        <p className="text-sm text-[#6b7280]">{error || "No data available"}</p>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-1.5" />
          Retry
        </Button>
      </div>
    );
  }

  const ta = data.technical_assessment;
  const hos = data.hands_on_simulation;
  const scoring = data.scoring;
  const grandTotal = ta.total + hos.total;
  const grandCompleted = ta.completed + hos.completed;
  const overallRate =
    grandTotal > 0 ? Math.round((grandCompleted / grandTotal) * 100) : 0;

  const emptyState = grandTotal === 0;

  return (
    <div
      className="max-w-[1600px] mx-auto px-6 py-6 space-y-6"
      data-testid="ta-hos-tab"
    >
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#1f2937]">
            Technical Assessment & Hands-On Simulation
          </h2>
          <p className="text-sm text-[#6b7280] mt-0.5">
            Real-time progress for TA and HOS modules across the organization
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchData}
          className="text-[#4b5563] border-[#e5e7eb]"
          data-testid="ta-refresh-btn"
        >
          <RefreshCw className="w-4 h-4 mr-1.5" />
          Refresh
        </Button>
      </div>

      {emptyState ? (
        /* Empty state — no candidates uploaded yet */
        <Card
          className="border border-dashed border-[#d1d5db] bg-[#f9fafb]"
          data-testid="ta-empty-state"
        >
          <CardContent className="py-16 flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-[#00AEEF]/10 flex items-center justify-center">
              <FileCode2 className="w-8 h-8 text-[#00AEEF]" />
            </div>
            <h3 className="text-base font-semibold text-[#1f2937]">
              No Assessments Uploaded Yet
            </h3>
            <p className="text-sm text-[#6b7280] max-w-md text-center">
              Technical Assessment and Hands-On Simulation candidates will
              appear here once they are uploaded via the Admin dashboard.
            </p>
            <div className="flex gap-3 mt-2">
              <Badge
                variant="outline"
                className="text-xs border-[#d1d5db] text-[#6b7280]"
              >
                {scoring.active_rubrics} Active Rubrics
              </Badge>
              <Badge
                variant="outline"
                className="text-xs border-[#d1d5db] text-[#6b7280]"
              >
                AI Scoring Ready
              </Badge>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Top-level stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              icon={FileCode2}
              label="Total Candidates"
              value={grandTotal}
              sub={`TA: ${ta.total} | HOS: ${hos.total}`}
              color="#00AEEF"
              testId="stat-total-candidates"
            />
            <StatCard
              icon={CheckCircle2}
              label="Completed"
              value={grandCompleted}
              sub={`${overallRate}% completion rate`}
              color="#10b981"
              testId="stat-completed"
            />
            <StatCard
              icon={Award}
              label="Scored"
              value={scoring.total_scored}
              sub={
                scoring.avg_composite_score > 0
                  ? `Avg score: ${scoring.avg_composite_score}/100`
                  : "No scores yet"
              }
              color="#8b5cf6"
              testId="stat-scored"
            />
            <StatCard
              icon={Shield}
              label="Integrity Events"
              value={scoring.total_events || data.integrity.total_events}
              sub={`${scoring.active_rubrics} active rubrics`}
              color="#f59e0b"
              testId="stat-integrity"
            />
          </div>

          {/* TA vs HOS side-by-side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Technical Assessment */}
            <Card
              className="border border-[#e5e7eb]"
              data-testid="ta-card"
            >
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <FileCode2 className="w-5 h-5 text-[#00AEEF]" />
                  <CardTitle className="text-sm font-semibold text-[#1f2937]">
                    Technical Assessment
                  </CardTitle>
                  <Badge
                    variant="outline"
                    className="ml-auto text-xs text-[#00AEEF] border-[#00AEEF]/30"
                  >
                    {ta.total} candidates
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <CompletionBar
                  label="Completed"
                  completed={ta.completed}
                  total={ta.total}
                  color="#10b981"
                />
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-[#f9fafb] rounded-lg p-3">
                    <p className="text-lg font-bold text-[#1f2937]">
                      {ta.completed}
                    </p>
                    <p className="text-[10px] text-[#6b7280] uppercase tracking-wider">
                      Done
                    </p>
                  </div>
                  <div className="bg-[#f9fafb] rounded-lg p-3">
                    <p className="text-lg font-bold text-[#1f2937]">
                      {ta.in_progress}
                    </p>
                    <p className="text-[10px] text-[#6b7280] uppercase tracking-wider">
                      In Progress
                    </p>
                  </div>
                  <div className="bg-[#f9fafb] rounded-lg p-3">
                    <p className="text-lg font-bold text-[#1f2937]">
                      {ta.not_started}
                    </p>
                    <p className="text-[10px] text-[#6b7280] uppercase tracking-wider">
                      Not Started
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Hands-On Simulation */}
            <Card
              className="border border-[#e5e7eb]"
              data-testid="hos-card"
            >
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Monitor className="w-5 h-5 text-[#8b5cf6]" />
                  <CardTitle className="text-sm font-semibold text-[#1f2937]">
                    Hands-On Simulation
                  </CardTitle>
                  <Badge
                    variant="outline"
                    className="ml-auto text-xs text-[#8b5cf6] border-[#8b5cf6]/30"
                  >
                    {hos.total} candidates
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <CompletionBar
                  label="Completed"
                  completed={hos.completed}
                  total={hos.total}
                  color="#8b5cf6"
                />
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-[#f9fafb] rounded-lg p-3">
                    <p className="text-lg font-bold text-[#1f2937]">
                      {hos.completed}
                    </p>
                    <p className="text-[10px] text-[#6b7280] uppercase tracking-wider">
                      Done
                    </p>
                  </div>
                  <div className="bg-[#f9fafb] rounded-lg p-3">
                    <p className="text-lg font-bold text-[#1f2937]">
                      {hos.in_progress}
                    </p>
                    <p className="text-[10px] text-[#6b7280] uppercase tracking-wider">
                      In Progress
                    </p>
                  </div>
                  <div className="bg-[#f9fafb] rounded-lg p-3">
                    <p className="text-lg font-bold text-[#1f2937]">
                      {hos.not_started}
                    </p>
                    <p className="text-[10px] text-[#6b7280] uppercase tracking-wider">
                      Not Started
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Breakdown tables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* By Job Family */}
            <Card
              className="border border-[#e5e7eb]"
              data-testid="ta-by-job-family"
            >
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#00AEEF]" />
                  <CardTitle className="text-sm font-semibold text-[#1f2937]">
                    Progress by Job Family
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {data.by_job_family.length === 0 ? (
                  <p className="text-sm text-[#9ca3af] text-center py-6">
                    No job family data yet
                  </p>
                ) : (
                  <div className="space-y-3">
                    {data.by_job_family.map((jf) => {
                      const pct =
                        jf.total > 0
                          ? Math.round((jf.completed / jf.total) * 100)
                          : 0;
                      return (
                        <div
                          key={jf.job_family}
                          className="flex items-center gap-3"
                        >
                          <span className="text-sm text-[#4b5563] w-40 truncate font-medium">
                            {jf.job_family}
                          </span>
                          <div className="flex-1">
                            <Progress value={pct} className="h-2" />
                          </div>
                          <div className="flex items-center gap-2 text-xs text-[#6b7280] min-w-[100px] justify-end">
                            <StatusDot color="#10b981" />
                            <span>{jf.completed}</span>
                            <StatusDot color="#f59e0b" />
                            <span>{jf.in_progress}</span>
                            <StatusDot color="#d1d5db" />
                            <span>{jf.not_started}</span>
                          </div>
                        </div>
                      );
                    })}
                    <div className="flex items-center gap-4 pt-2 border-t text-[10px] text-[#9ca3af] uppercase tracking-wider">
                      <span className="flex items-center gap-1">
                        <StatusDot color="#10b981" /> Completed
                      </span>
                      <span className="flex items-center gap-1">
                        <StatusDot color="#f59e0b" /> In Progress
                      </span>
                      <span className="flex items-center gap-1">
                        <StatusDot color="#d1d5db" /> Not Started
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* By Region */}
            <Card
              className="border border-[#e5e7eb]"
              data-testid="ta-by-region"
            >
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <ArrowUpRight className="w-5 h-5 text-[#00AEEF]" />
                  <CardTitle className="text-sm font-semibold text-[#1f2937]">
                    Regional Deployment
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {data.by_region.length === 0 ? (
                  <p className="text-sm text-[#9ca3af] text-center py-6">
                    No regional data yet
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[#e5e7eb]">
                          <th className="text-left py-2 text-[10px] text-[#9ca3af] uppercase tracking-wider font-medium">
                            Region
                          </th>
                          <th className="text-center py-2 text-[10px] text-[#9ca3af] uppercase tracking-wider font-medium">
                            TA
                          </th>
                          <th className="text-center py-2 text-[10px] text-[#9ca3af] uppercase tracking-wider font-medium">
                            HOS
                          </th>
                          <th className="text-center py-2 text-[10px] text-[#9ca3af] uppercase tracking-wider font-medium">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.by_region.map((r) => (
                          <tr
                            key={r.region}
                            className="border-b border-[#f3f4f6] last:border-0"
                          >
                            <td className="py-2.5 text-[#4b5563] font-medium capitalize">
                              {r.region?.replace(/_/g, " ") || "Unknown"}
                            </td>
                            <td className="py-2.5 text-center">
                              <span className="text-[#1f2937] font-medium">
                                {r.ta_completed}
                              </span>
                              <span className="text-[#9ca3af]">
                                /{r.ta_total}
                              </span>
                            </td>
                            <td className="py-2.5 text-center">
                              <span className="text-[#1f2937] font-medium">
                                {r.hos_completed}
                              </span>
                              <span className="text-[#9ca3af]">
                                /{r.hos_total}
                              </span>
                            </td>
                            <td className="py-2.5 text-center font-semibold text-[#1f2937]">
                              {r.ta_total + r.hos_total}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent Scores */}
          {data.recent_scores.length > 0 && (
            <Card
              className="border border-[#e5e7eb]"
              data-testid="ta-recent-scores"
            >
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-[#8b5cf6]" />
                  <CardTitle className="text-sm font-semibold text-[#1f2937]">
                    Recent Scoring Results
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#e5e7eb]">
                        <th className="text-left py-2 text-[10px] text-[#9ca3af] uppercase tracking-wider font-medium">
                          Candidate
                        </th>
                        <th className="text-left py-2 text-[10px] text-[#9ca3af] uppercase tracking-wider font-medium">
                          Type
                        </th>
                        <th className="text-left py-2 text-[10px] text-[#9ca3af] uppercase tracking-wider font-medium">
                          Job Family
                        </th>
                        <th className="text-center py-2 text-[10px] text-[#9ca3af] uppercase tracking-wider font-medium">
                          Score
                        </th>
                        <th className="text-right py-2 text-[10px] text-[#9ca3af] uppercase tracking-wider font-medium">
                          Scored At
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recent_scores.map((s, i) => (
                        <tr
                          key={i}
                          className="border-b border-[#f3f4f6] last:border-0"
                        >
                          <td className="py-2.5 text-[#1f2937] font-medium">
                            {s.candidate_name}
                          </td>
                          <td className="py-2.5">
                            <Badge
                              variant="outline"
                              className={`text-[10px] ${
                                s.assessment_type === "technical_assessment"
                                  ? "text-[#00AEEF] border-[#00AEEF]/30"
                                  : "text-[#8b5cf6] border-[#8b5cf6]/30"
                              }`}
                            >
                              {s.assessment_type === "technical_assessment"
                                ? "TA"
                                : "HOS"}
                            </Badge>
                          </td>
                          <td className="py-2.5 text-[#6b7280]">
                            {s.job_family}
                          </td>
                          <td className="py-2.5 text-center">
                            <span
                              className={`inline-flex items-center justify-center w-12 h-7 rounded-md text-xs font-bold ${
                                s.composite_score >= 80
                                  ? "bg-emerald-50 text-emerald-700"
                                  : s.composite_score >= 60
                                  ? "bg-amber-50 text-amber-700"
                                  : "bg-red-50 text-red-700"
                              }`}
                            >
                              {s.composite_score}
                            </span>
                          </td>
                          <td className="py-2.5 text-right text-[#9ca3af] text-xs">
                            {s.scored_at
                              ? new Date(s.scored_at).toLocaleDateString()
                              : "N/A"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
